import moment from 'moment'
import { useMemo, useEffect, useRef, useState } from 'react'

import { getRunStage } from 'components/scenarios/utils'
import useAgent from 'hooks/use-agent'
import useAuth from 'hooks/use-auth'

export default function useScenarioResource({
  interval = 10000,
  shouldCache = false,
  enabled = true
} = {}) {
  const { currentTeamId, session, isPaywallActive } = useAuth()
  const currentCompletedRuns = useRef()
  const registeredTeamId = useRef(currentTeamId)
  const [newCompletedScenarios, setNewCompletedScenarios] = useState({})

  const yesterday = moment().subtract(24, 'hours').startOf('day').toISOString()
  const today = moment().endOf('day').toISOString()

  const isEnabled = enabled && !isPaywallActive

  const {
    data: completedRuns,
    isLoading: isCompletedRunsLoading,
    hasError,
    doFetch: doFetchCompletedRuns
  } = useAgent(
    `/scenarios/runs?state=Terminal&includeArchived=false&teamId=${currentTeamId}&startDate=${encodeURIComponent(
      yesterday
    )}&endDate=${encodeURIComponent(today)}`,

    null,
    {
      shouldCache,
      interval,
      skipRequest: !isEnabled
    }
  )

  const completedScenariosAwaitingResults = useMemo(() => {
    if (isCompletedRunsLoading) {
      // use have existing data while refreshing so that we don't clear the list out each time
      return Array.isArray(completedRuns)
        ? completedRuns?.length > 0
          ? completedRuns.filter(
              (run) => Object.keys(run?.results ?? {}).length === 0
            )
          : []
        : []
    }

    return completedRuns?.length > 0
      ? completedRuns.filter(
          (run) => Object.keys(run?.results ?? {}).length === 0
        )
      : []
  }, [isCompletedRunsLoading, completedRuns])

  useEffect(() => {
    // Queue in the new notifications of completed scenarios
    const addCompletedScenariosNotifications = (
      previousValues,
      newNotificationCount,
      newCompletedRuns
    ) => {
      const newCompletedScenariosNotifications = Object.assign(
        {},
        previousValues
      )
      for (let i = 0; newNotificationCount > i; i++) {
        if (newCompletedRuns[i].created_by === session.identifier) {
          newCompletedScenariosNotifications[
            `${newCompletedRuns[i].scenario_id}|${newCompletedRuns[i].run_number}`
          ] = {
            runInfo: newCompletedRuns[i],
            notificationStatus: 'new'
          }
        }
      }
      return newCompletedScenariosNotifications
    }
    if (
      !isCompletedRunsLoading &&
      (completedRuns === null || Array.isArray(completedRuns))
    ) {
      const filteredCompletedRuns = completedRuns
        ? completedRuns.filter(
            (scenario) => getRunStage(scenario) === 'Completed'
          )
        : null

      // Conditions:
      // only proceed the difference between polling results in the queue if:
      // the loading == false
      // the completedRuns and previous run have been processed
      // the previous runs length is less than the newly polled runs length
      // and the change cas not caused by the switching between teams

      if (
        currentCompletedRuns.current &&
        filteredCompletedRuns !== null &&
        currentCompletedRuns.current.length < filteredCompletedRuns.length &&
        filteredCompletedRuns.length > 0 &&
        filteredCompletedRuns[0].org_id === registeredTeamId.current
      ) {
        const newCompletedScenariosCount =
          filteredCompletedRuns.length - currentCompletedRuns.current.length

        setNewCompletedScenarios((previousValue) =>
          addCompletedScenariosNotifications(
            previousValue,
            newCompletedScenariosCount,
            filteredCompletedRuns
          )
        )
      }
      currentCompletedRuns.current =
        registeredTeamId.current !== currentTeamId
          ? undefined
          : filteredCompletedRuns

      registeredTeamId.current = currentTeamId
    }
  }, [
    completedRuns,
    isCompletedRunsLoading,
    currentTeamId,
    registeredTeamId,
    session.identifier
  ])

  const returnState = useMemo(() => {
    return {
      newCompletedScenarios: newCompletedScenarios,
      setNewCompletedScenarios: setNewCompletedScenarios,
      completedRuns: completedRuns,
      completedScenariosAwaitingResults: completedScenariosAwaitingResults,
      doFetchCompletedRuns: doFetchCompletedRuns,
      isLoading: isCompletedRunsLoading,
      hasError
    }
  }, [
    setNewCompletedScenarios,
    newCompletedScenarios,
    completedRuns,
    completedScenariosAwaitingResults,
    doFetchCompletedRuns,
    isCompletedRunsLoading,
    hasError
  ])

  return returnState
}
